<template>
  <div class="grouped-table">
    <div
      v-show="hasOverflow"
      class="scroll-top"
      ref="topRef"
      @scroll="onTopScroll"
    >
      <div class="scroll-content" :style="{ width: `${scrollWidth}px` }"></div>
    </div>
    <div ref="hostRef">
      <a-table
        :data-source="groups"
        :columns="groupColumns"
        :row-key="groupRowKey"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 'max-content' }"
        :expanded-row-keys="expandedRowKeys"
        @expand="onExpand"
        @change="onTableChange"
      >
        <template
          v-for="slotName in groupSlots"
          :key="slotName"
          #[slotName]="slotProps"
        >
          <slot :name="slotName" v-bind="slotProps" />
        </template>

        <!-- Same markup and classes as the default icon, but hidden for
             groups without items, which have nothing to show. -->
        <template #expandIcon="{ expanded, record, onExpand }">
          <span
            v-if="hasGroupItems(record, itemsKey)"
            role="button"
            tabindex="0"
            :aria-label="expanded ? 'Contraer' : 'Expandir'"
            :class="[
              'ant-table-row-expand-icon',
              expanded ? 'ant-table-row-expanded' : 'ant-table-row-collapsed'
            ]"
            @click="(event) => onExpand(record, event)"
            @keydown.enter="(event) => onExpand(record, event)"
          />
          <span v-else class="ant-table-row-expand-icon ant-table-row-spaced" />
        </template>

        <template #expandedRowRender="{ record }">
          <div class="grouped-table__detail" :style="detailStyle">
            <p v-if="detailTitle" class="grouped-table__detail-title">
              {{ detailTitle(record) }}
            </p>
            <grouped-table-items
              :group="record"
              :items="record[itemsKey] || []"
              :columns="itemColumns"
              :row-key="itemRowKey"
            >
              <template
                v-for="slotName in itemSlots"
                :key="slotName"
                #[slotName]="slotProps"
              >
                <slot :name="slotName" v-bind="slotProps" />
              </template>
            </grouped-table-items>
          </div>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineExpose,
  defineProps,
  nextTick,
  ref,
  useSlots,
  watch
} from 'vue';
import useTopScrollSync from '@/composables/use-top-scroll-sync';
import GroupedTableItems from './grouped-table-items.vue';
import {
  getInitialExpandedKeys,
  getRowKeyValue,
  hasGroupItems
} from './grouped-table-helpers';

const props = defineProps({
  groups: {
    type: Array,
    default: () => []
  },
  groupColumns: {
    type: Array,
    default: () => []
  },
  groupRowKey: {
    type: [String, Function],
    default: 'key'
  },
  itemsKey: {
    type: String,
    default: 'items'
  },
  itemColumns: {
    type: Array,
    default: () => []
  },
  itemRowKey: {
    type: [String, Function],
    default: 'key'
  },
  // Slot names rendered by the items table; every other slot goes to the groups table.
  itemSlotNames: {
    type: Array,
    default: () => []
  },
  defaultExpanded: {
    type: String,
    default: 'none',
    validator: (value) => ['none', 'first', 'all'].includes(value)
  },
  detailTitle: {
    type: Function,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  pageSize: {
    type: Number,
    default: 10
  }
});

const emit = defineEmits(['expand']);

const slots = useSlots();
const itemSlots = computed(() =>
  Object.keys(slots).filter((name) => props.itemSlotNames.includes(name))
);
const groupSlots = computed(() =>
  Object.keys(slots).filter(
    (name) =>
      !props.itemSlotNames.includes(name) && name !== 'expandedRowRender'
  )
);

const currentPage = ref(1);
const expandedRowKeys = ref([]);

const pagination = computed(() => ({
  current: currentPage.value,
  pageSize: props.pageSize,
  showSizeChanger: false,
  hideOnSinglePage: false
}));

const currentPageGroups = () => {
  const start = (currentPage.value - 1) * props.pageSize;
  return props.groups.slice(start, start + props.pageSize);
};

const resetExpanded = () => {
  expandedRowKeys.value = getInitialExpandedKeys(
    currentPageGroups(),
    props.groupRowKey,
    props.defaultExpanded,
    props.itemsKey
  );
};

const {
  topRef,
  hostRef,
  scrollWidth,
  visibleWidth,
  hasOverflow,
  onTopScroll,
  refresh
} = useTopScrollSync();

// ant-design-vue renders the expanded row after an empty cell for the expand
// icon column, so the detail starts shifted to the right. That shift is
// measured to keep the detail inside the visible area of the groups table:
// it is pinned at the same distance from the left edge and its width leaves
// that distance out, so the items table never goes past the right edge.
// Until a valid measure exists, a conservative default keeps it contained.
const DEFAULT_DETAIL_OFFSET = 64;
const detailOffset = ref(DEFAULT_DETAIL_OFFSET);

const measureDetailOffset = async () => {
  await nextTick();
  // Measured from our own detail element: its parent is the cell that holds it.
  const detail = hostRef.value?.querySelector('.grouped-table__detail');
  const cell = detail?.parentElement;
  const table = cell?.closest('table');
  if (!cell || !table) return;
  const paddingLeft = parseFloat(getComputedStyle(cell).paddingLeft) || 0;
  const offset =
    cell.getBoundingClientRect().left -
    table.getBoundingClientRect().left +
    paddingLeft;
  if (Number.isFinite(offset) && offset >= 0) detailOffset.value = offset;
};

const detailStyle = computed(() => {
  if (!visibleWidth.value) return {};
  return {
    left: `${detailOffset.value}px`,
    width: `${Math.max(visibleWidth.value - detailOffset.value, 0)}px`
  };
});

const onExpand = (expanded, record) => {
  const key = getRowKeyValue(record, props.groupRowKey);
  expandedRowKeys.value = expanded
    ? [...expandedRowKeys.value, key]
    : expandedRowKeys.value.filter((value) => value !== key);
  emit('expand', { expanded, record });
};

const onTableChange = (pageInfo) => {
  if (pageInfo?.current && pageInfo.current !== currentPage.value) {
    currentPage.value = pageInfo.current;
    resetExpanded();
  }
};

const expandAll = () => {
  expandedRowKeys.value = currentPageGroups()
    .filter((group) => hasGroupItems(group, props.itemsKey))
    .map((group) => getRowKeyValue(group, props.groupRowKey));
};

const collapseAll = () => {
  expandedRowKeys.value = [];
};

watch(
  () => props.groups,
  async () => {
    const pages = Math.max(1, Math.ceil(props.groups.length / props.pageSize));
    if (currentPage.value > pages) currentPage.value = pages;
    resetExpanded();
    await refresh();
    await measureDetailOffset();
  },
  { immediate: true }
);

watch(expandedRowKeys, async () => {
  await refresh();
  await measureDetailOffset();
});

watch(visibleWidth, measureDetailOffset);

defineExpose({ expandAll, collapseAll });
</script>

<style scoped>
.scroll-top {
  height: 18px;
  margin-bottom: 6px;
  overflow-x: scroll;
  overflow-y: hidden;
  border-radius: 6px;
}

.scroll-content {
  height: 1px;
}

.grouped-table :deep(.ant-table-expanded-row > td) {
  background: #f7f9fb;
}

/* Expand/collapse button, highlighted with the platform primary color */
.grouped-table :deep(.ant-table-row-expand-icon) {
  width: 22px;
  height: 22px;
  line-height: 19px;
  font-size: 16px;
  font-weight: 600;
  color: #2521e5;
  background: #eeeafe;
  border: 1px solid #2521e5;
  border-radius: 4px;
  transition:
    color 0.2s,
    background-color 0.2s;
}

.grouped-table :deep(.ant-table-row-expand-icon:hover),
.grouped-table :deep(.ant-table-row-expand-icon:focus-visible),
.grouped-table :deep(.ant-table-row-expand-icon.ant-table-row-expanded) {
  color: #ffffff;
  background: #2521e5;
}

.grouped-table__detail {
  position: sticky;
  left: 0;
  box-sizing: border-box;
  padding: 4px 16px 8px;
}

.grouped-table__detail-title {
  margin-bottom: 8px;
  color: #59636c;
}
</style>
